import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchGetChatUsers } from 'api/chat';
import { ChatViewId } from 'hooks/useCurrentChatView';
import { dispatch, store } from 'store';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { ChatUserLabel } from 'types/chat-users/chat-user-label';
import { ChatUsersFilters, ChatUsersFiltersRequest } from 'types/chat-users/filters';
import { HistoryMessage, MessageModel } from 'types/chat/messages/message';
import { ApplicationUser } from 'types/company/applicationUser';
import { fetcher } from 'utils/axios';
import { doesUserMatchAdditionalFilters, doesUserMatchCurrentFilters } from 'utils/chat';

export const initialFilters: ChatUsersFilters = {
  pageOffset: 1,
  searchQuery: '',
  label: '',
  open: 'all',
  isOnlyUnread: false,
  assigneeFilter: [],
  dontDisturb: false,
  isBot: false,
};

const initialState: {
  selectedChatUser: ChatUserModel | null;
  chatUsers: {
    myChatUsers: ChatUserModel[];
    notMyChatUsers: ChatUserModel[];
    unassignedChatUsers: ChatUserModel[];
    botsChatUsers: ChatUserModel[];
    allChatUsers: ChatUserModel[];
    customChatUsers: ChatUserModel[];
    loading: boolean;
  };
  areUsersRequested: {
    myChatUsers: boolean;
    notMyChatUsers: boolean;
    unassignedChatUsers: boolean;
    botsChatUsers: boolean;
    allChatUsers: boolean;
    customChatUsers: boolean;
  };
  chatUsersFilters: ChatUsersFilters;
  chatUsersSavedMessages: Array<{
    id: string;
    messages: Array<HistoryMessage | MessageModel>;
  }>;
  lastRequestedCustomViewId?: string;
} = {
  selectedChatUser: null,
  chatUsers: {
    myChatUsers: [],
    notMyChatUsers: [],
    unassignedChatUsers: [],
    botsChatUsers: [],
    allChatUsers: [],
    customChatUsers: [],
    loading: false,
  },
  areUsersRequested: {
    myChatUsers: false,
    notMyChatUsers: false,
    unassignedChatUsers: false,
    botsChatUsers: false,
    allChatUsers: false,
    customChatUsers: false,
  },
  chatUsersFilters: initialFilters,
  chatUsersSavedMessages: [],
  lastRequestedCustomViewId: undefined,
};

const selectedChatUserSlice = createSlice({
  name: 'selectedChatUser',
  initialState: initialState,
  reducers: {
    setChatUsers(
      state,
      action: PayloadAction<{
        myChatUsers?: ChatUserModel[];
        notMyChatUsers?: ChatUserModel[];
        unassignedChatUsers?: ChatUserModel[];
        botsChatUsers?: ChatUserModel[];
        allChatUsers?: ChatUserModel[];
        customChatUsers?: ChatUserModel[];
      }>,
    ) {
      if (action.payload.notMyChatUsers) state.chatUsers.notMyChatUsers = action.payload.notMyChatUsers;
      if (action.payload.unassignedChatUsers)
        state.chatUsers.unassignedChatUsers = action.payload.unassignedChatUsers;
      if (action.payload.botsChatUsers) state.chatUsers.botsChatUsers = action.payload.botsChatUsers;
      if (action.payload.allChatUsers) state.chatUsers.allChatUsers = action.payload.allChatUsers;
      if (action.payload.myChatUsers) state.chatUsers.myChatUsers = action.payload.myChatUsers;
      if (action.payload.customChatUsers) state.chatUsers.customChatUsers = action.payload.customChatUsers;
    },
    setChatUsersLoading(state, action: PayloadAction<boolean>) {
      state.chatUsers.loading = action.payload;
    },
    storeSetChatUsersRequested(
      state,
      action: PayloadAction<Partial<Record<keyof typeof initialState.areUsersRequested, boolean>>>,
    ) {
      const params = action.payload;
      Object.assign(state.areUsersRequested, params);
    },
    storeUpdateSingleChatUser(state, action: PayloadAction<{ id: number; data: Partial<ChatUserModel> }>) {
      const { id, data } = action.payload;
      if (state.selectedChatUser && state.selectedChatUser.Id === id) {
        Object.assign(state.selectedChatUser, data);
      }
      const states = Object.keys(state.chatUsers).filter((objKey) => objKey !== 'loading') as Array<
        keyof Omit<typeof state.chatUsers, 'loading'>
      >;
      states.forEach((key) => {
        const arr = state.chatUsers[key];
        const idx = arr.findIndex((chatUser) => chatUser.Id === id);
        if (idx !== -1) {
          Object.assign(arr[idx], data);
        }
      });
    },
    storeUpdateSingleChatUserByPhone(
      state,
      action: PayloadAction<{ phone: string; data: Partial<ChatUserModel> }>,
    ) {
      const { phone, data } = action.payload;
      if (state.selectedChatUser && state.selectedChatUser.UserDisplayPhoneNumber === phone) {
        Object.assign(state.selectedChatUser, data);
      }
      const states = Object.keys(state.chatUsers).filter((objKey) => objKey !== 'loading') as Array<
        keyof Omit<typeof state.chatUsers, 'loading'>
      >;
      states.forEach((key) => {
        const arr = state.chatUsers[key];
        const idx = arr.findIndex((chatUser) => chatUser.UserDisplayPhoneNumber === phone);
        if (idx !== -1) {
          Object.assign(arr[idx], data);
        }
      });
    },
    setSelectedChatUser(state, action: PayloadAction<ChatUserModel | null>) {
      state.selectedChatUser = action.payload;
    },
    handleChatUsersFilters(state, action: PayloadAction<Partial<ChatUsersFilters>>) {
      Object.assign(state.chatUsersFilters, action.payload);
    },
    setChatUsersSavedMessages(
      state,
      action: PayloadAction<Array<{ id: string; messages: Array<HistoryMessage | MessageModel> }>>,
    ) {
      const { payload } = action;
      state.chatUsersSavedMessages = payload;
    },
    handleUserSavedMessages(
      state,
      action: PayloadAction<{ id: string; messages: Array<HistoryMessage | MessageModel> }>,
    ) {
      const { id, messages } = action.payload;

      const index = state.chatUsersSavedMessages.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.chatUsersSavedMessages[index].messages = messages;
      } else {
        state.chatUsersSavedMessages.push({ id, messages });
      }
    },
    saveSingleMessageToUser(
      state,
      action: PayloadAction<{ id: string; message: HistoryMessage | MessageModel }>,
    ) {
      const { id, message } = action.payload;
      const index = state.chatUsersSavedMessages.findIndex((item) => item.id === id);
      if (index !== -1) {
        const messagesList = state.chatUsersSavedMessages[index].messages;
        const existingMessageIndex = messagesList.findIndex((msg) => {
          const messageIdMatch = msg.Id && message.Id && msg.Id === message.Id;

          const msgTempId = msg.TempId || msg.tempId;
          const messageTempId = message.TempId || message.tempId;
          const tempIdMatch = msgTempId != null && messageTempId != null && msgTempId === messageTempId;
          return messageIdMatch || tempIdMatch;
        });

        if (existingMessageIndex !== -1) {
          // Update the existing message
          state.chatUsersSavedMessages[index].messages[existingMessageIndex] = {
            ...messagesList[existingMessageIndex],
            ...message,
          };
        } else {
          // Add the new message
          state.chatUsersSavedMessages[index].messages.push(message);
        }
      } else {
        // Create a new entry for the user if it doesn't exist
        state.chatUsersSavedMessages.push({ id, messages: [message] });
      }
    },

    resetChat(state) {
      return initialState;
    },
    setLastRequestedCustomViewId(state, action: PayloadAction<string | undefined>) {
      state.lastRequestedCustomViewId = action.payload;
    },
  },
});

export default selectedChatUserSlice.reducer;
export const {
  setChatUsers,
  setChatUsersLoading,
  storeSetChatUsersRequested,
  storeUpdateSingleChatUser,
  storeUpdateSingleChatUserByPhone,
  setSelectedChatUser,
  setChatUsersSavedMessages,
  handleChatUsersFilters,
  handleUserSavedMessages,
  saveSingleMessageToUser,
  resetChat,
  setLastRequestedCustomViewId,
} = selectedChatUserSlice.actions;

//* handlers
export const setChatUsersRequested = (
  params: Partial<Record<keyof typeof initialState.areUsersRequested, boolean>>,
) => {
  dispatch(storeSetChatUsersRequested({ ...params }));
};
export const updateSingleChatUser = (id: number, data: Partial<ChatUserModel>) => {
  dispatch(storeUpdateSingleChatUser({ id, data }));
};
export const updateSingleChatUserByPhone = (phone: string, data: Partial<ChatUserModel>) => {
  dispatch(storeUpdateSingleChatUserByPhone({ phone, data }));
};

//* fetchers
export async function getChatUsers(useLoader: boolean = true, view?: ChatViewId) {
  if (useLoader) dispatch(setChatUsersLoading(true));
  try {
    let params: ChatUsersFiltersRequest;
    const chatUsersFilters = store.getState().chat.chatUsersFilters;
    
    // עבור תצוגות מותאמות, נביא יותר שיחות כדי לסנן אותן בצד הלקוח
    const pageSize = view === 'custom' ? 1000 : 30;
    
    // בדוק אם יש פילטר תגית (LableName) בתצוגות מותאמות ושלח אותו ל-API
    let labelForApi = view === 'custom' ? '' : chatUsersFilters.label;
    let assigneeFilterForApi = view === 'custom' ? [] : chatUsersFilters.assigneeFilter;
    
    if (view === 'custom') {
      // עבור תצוגות מותאמות, בדוק גם את הפילטר הרגיל של התגית (label)
      // וגם את הפילטרים של התצוגה המותאמת (filters)
      if (chatUsersFilters.label && chatUsersFilters.label !== '') {
        labelForApi = chatUsersFilters.label;
      } else if (chatUsersFilters.filters) {
      const labelFilter = chatUsersFilters.filters.find((f) => f.key === 'LableName' && f.value != null);
      if (labelFilter) {
        labelForApi = String(labelFilter.value); // הערך הוא כבר JSON.stringify(label)
        }
      }
      
      // בדוק אם יש פילטר משויכת ל (NotificationContactUser) בתצוגות מותאמות
      if (chatUsersFilters.assigneeFilter && chatUsersFilters.assigneeFilter.length > 0) {
        assigneeFilterForApi = chatUsersFilters.assigneeFilter;
      } else if (chatUsersFilters.filters) {
      const assigneeFilter = chatUsersFilters.filters.find((f) => f.key === 'NotificationContactUser' && f.value != null);
      if (assigneeFilter) {
        // הערך יכול להיות string או array
        if (Array.isArray(assigneeFilter.value)) {
          assigneeFilterForApi = assigneeFilter.value;
        } else {
          assigneeFilterForApi = [assigneeFilter.value];
          }
        }
      }
    }
    
    if (chatUsersFilters.searchQuery.length) {
      params = {
        page_number: pageSize,
        page_offset: 0,
        filter_by_open: chatUsersFilters.open, // נשלח גם בתצוגות מותאמות
        order_by: 'last_message_date',
        order_descending: true,
        search_term: chatUsersFilters.searchQuery, // נשלח גם בתצוגות מותאמות
        label: labelForApi, // נשלח גם בתצוגות מותאמות אם יש פילטר תגית
        isOnlyUnread: chatUsersFilters.isOnlyUnread, // נשלח גם בתצוגות מותאמות
        assigneeFilter: assigneeFilterForApi, // נשלח גם בתצוגות מותאמות אם יש פילטר משויכת ל
        dontDisturb: chatUsersFilters.dontDisturb,
        filters: [], // עבור תצוגות מותאמות הפילטרים יתבצעו בצד הלקוח
        is_bot: view === 'custom' ? false : (chatUsersFilters.isBot ?? false), // עבור תצוגות מותאמות לא נסנן בשרת
      };
    } else {
      params = {
        page_number: view === 'custom' ? pageSize : 30 * chatUsersFilters.pageOffset,
        page_offset: 0,
        filter_by_open: chatUsersFilters.open, // נשלח גם בתצוגות מותאמות
        order_by: 'last_message_date',
        order_descending: true,
        search_term: chatUsersFilters.searchQuery, // נשלח גם בתצוגות מותאמות
        label: labelForApi, // נשלח גם בתצוגות מותאמות אם יש פילטר תגית
        isOnlyUnread: chatUsersFilters.isOnlyUnread, // נשלח גם בתצוגות מותאמות
        assigneeFilter: assigneeFilterForApi, // נשלח גם בתצוגות מותאמות אם יש פילטר משויכת ל
        dontDisturb: chatUsersFilters.dontDisturb,
        filters: [], // עבור תצוגות מותאמות הפילטרים יתבצעו בצד הלקוח
        is_bot: view === 'custom' ? false : (chatUsersFilters.isBot ?? false), // עבור תצוגות מותאמות לא נסנן בשרת
      };
    }

    console.log('params getChatUsers', params);
    const data = await fetchGetChatUsers(params);
    console.log('data getChatUsers', data);
    if (data) {
      if (view === 'all') {
        dispatch(
          setChatUsers({
            allChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          allChatUsers: true,
        });
      } else if (view === 'my') {
        dispatch(
          setChatUsers({
            myChatUsers: data?.myChatUsers?.chatUsers ? [...data.myChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          myChatUsers: true,
        });
      } else if (view === 'notMy') {
        // סינון שיחות של משתמשים אחרים (לא כולל שיחות לא משויכות)
        const currentUser = store.getState().user;
        const allChats = data?.allChatUsers?.chatUsers || [];
        const notMyChats = allChats.filter((chat) =>
          chat.NotificationContactUser && 
          chat.NotificationContactUser !== currentUser?.Udid
        );

        dispatch(
          setChatUsers({
            [`${view}ChatUsers`]: notMyChats,
          }),
        );
        setChatUsersRequested({
          [`${view}ChatUsers`]: true,
        });
      } else if (view === 'unassigned') {
        // סינון שיחות שלא משויכות לאף משתמש
        const allChats = data?.allChatUsers?.chatUsers || [];
        const unassignedChats = allChats.filter((chat) =>
          !chat.NotificationContactUser || chat.NotificationContactUser === null || chat.NotificationContactUser === undefined
        );

        dispatch(
          setChatUsers({
            [`${view}ChatUsers`]: unassignedChats,
          }),
        );
        setChatUsersRequested({
          [`${view}ChatUsers`]: true,
        });
      } else if (view === 'bots') {
        // סינון שיחות של בוטים בלבד
        const allChats = data?.allChatUsers?.chatUsers || [];
        const botChats = allChats.filter((chat) =>
          chat.isBot === true
        );

        dispatch(
          setChatUsers({
            [`${view}ChatUsers`]: botChats,
          }),
        );
        setChatUsersRequested({
          [`${view}ChatUsers`]: true,
        });
      } else if (view === 'custom') {
        // סינון שיחות לפי הפילטרים המותאמים והפילטרים הרגילים
        const allChats = data?.allChatUsers?.chatUsers || [];
        const chatUsersFilters = store.getState().chat.chatUsersFilters;

        console.log('chatUsersFilters custom', chatUsersFilters);
        console.log('allChats custom', allChats);
        // החל את הפילטרים המותאמים והפילטרים הרגילים על כל השיחות
        const filteredChats = allChats.filter((chat) => 
          // doesUserMatchAdditionalFilters(chatUsersFilters.filters || [], chat) &&
          doesUserMatchCurrentFilters(chat, 'customChatUsers')
        );

        console.log('filteredChats custom', filteredChats);

        dispatch(
          setChatUsers({
            customChatUsers: filteredChats,
          }),
        );
        setChatUsersRequested({
          customChatUsers: true,
        });
      } else {
        dispatch(
          setChatUsers({
            [`${view}ChatUsers`]: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
          }),
        );
        setChatUsersRequested({
          [`${view}ChatUsers`]: true,
        });
      }

      const selectedChatUser = store.getState().chat.selectedChatUser;
      if (selectedChatUser) {
        const updatedUser = data?.allChatUsers.chatUsers.find(
          (user: ChatUserModel) => user.Id === selectedChatUser.Id,
        );
        if (updatedUser) {
          dispatch(setSelectedChatUser(updatedUser));
        }
      }

      return {
        allChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
        myChatUsers: data?.myChatUsers?.chatUsers ? [...data.myChatUsers.chatUsers] : [],
      };
    }
  } catch (err) {
    console.warn('GetCompanyChatUsersAll error: ', err);
  } finally {
    if (useLoader) dispatch(setChatUsersLoading(false));
  }
}
export async function setNewUserName(phone: string, newName: string) {
  try {
    const formData = {
      phone,
      newName,
    };

    const data = await fetcher(
      'post',
      ['/api/services/UpdateChatUserName', { data: formData }],
      'history',
      // true,
    );

    if (data?.result) {
      updateSingleChatUserByPhone(phone, { FullName: newName });
      return true;
    } else {
      console.log('error editing name');
      return false;
    }
  } catch (error) {
    console.warn('setNewUserName error: ', error);
    return false;
  }
}
export async function handleSetUserLabel(userId: string, option: ChatUserLabel) {
  try {
    await fetcher(
      'post',
      ['/api/services/SetChatUserLabel', { data: { id: userId, labelName: JSON.stringify(option) } }],
      'history',
    );
  } catch (error) {
    console.log('SetChatUserLabel error: ', error);
  }
}
export async function handleSetUserConversationOpenStatus(userId: string, state: boolean) {
  const userData = store.getState().user;
  const params = {
    userglobalid: userData?.Udid,
    chatUserDisplayPhoneNumber: userId,
    isOpen: state,
  };

  try {
    await fetcher('post', ['/api/services/SetChatUserOpenStatus', { data: params }], 'history');
  } catch (error) {
    console.error(error);
  }
}
export async function handleSetUserAssignee(userId: string, assignee: ApplicationUser | null) {
  try {
    const user = store.getState().user;
    const data = await fetcher(
      'post',
      [
        '/api/services/SetUserChatNotificationContact',
        {
          data: {
            userglobalid: assignee ? assignee.Udid : null,
            displayPhoneNumber: userId,
            companyId: user?.Udid,
          },
        },
      ],
      'history',
    );

    if (data?.result) {
      updateSingleChatUserByPhone(userId, {
        NotificationContactName: assignee ? assignee.FullName : null,
        NotificationContactUser: assignee ? assignee.Udid : null,
      });
    }
  } catch (error) {
    console.log('SetUserChatNotificationContact error: ', error);
  }
}
